import { get } from "lodash"
import classNames from "classnames"
import { DEFAULT_METRIC_COLUMN_WIDTH } from "../constants"
import { VALUE_FORMATS } from "helpers/metrics/constants"

function getColumnType(metric) {
  const format = metric.format || metric.outputFormat

  switch (format) {
    case VALUE_FORMATS.NUMBER:
    case VALUE_FORMATS.PERCENT:
    case VALUE_FORMATS.PERCENT_100:
    case VALUE_FORMATS.CURRENCY:
      return "number"
    case VALUE_FORMATS.STRING:
    default:
      return "string"
  }
}

function createValueGetter(metric, columnType) {
  return (value, row) => {
    const contributor = row.contributor
    if (!contributor || !contributor.values) return null

    const rawValue = metric.dataGetter
      ? metric.dataGetter(contributor.values)
      : get(contributor.values, metric.dataPath)

    if (columnType === "number" && rawValue !== null && rawValue !== undefined) {
      return Number(rawValue)
    }

    return rawValue
  }
}

function createCellClassName(metric, metricCategories, groupEndFieldSet) {
  return () => {
    const categoryInfo = metricCategories?.[metric.category]
    const isLastInGroupFromCategory = categoryInfo ? metric.metricIndex === (categoryInfo.nbMetrics - 1) : false
    const isLastInGroupFromGroupingModel = groupEndFieldSet?.has?.(metric.id) || false
    const isLastInGroup = isLastInGroupFromGroupingModel || isLastInGroupFromCategory
    return classNames({
      "first-instrument": metric.isFirstInstrument,
      "group-end": isLastInGroup,
    })
  }
}

export const createMetricColumns = ({
  metricMetadata,
  metricCategories,
  groupEndFieldSet,
  sortMetricId,
  isAbsoluteSort,
  sortArrowDirection,
  onSortChange,
  data,
  MetricCellComponent,
  CustomHeaderComponent,
  savedDataGridState,
}) => {
  return metricMetadata.map((metric) => {
    const columnType = getColumnType(metric)

    const savedWidth = get(savedDataGridState, ["columns", "dimensions", metric.id, "width"])
    let columnWidth
    if (savedWidth && savedWidth > 0) {
      columnWidth = savedWidth
    } else if (metric.initialWidth) {
      columnWidth = metric.initialWidth
    }
    return {
      field: metric.id,
      headerName: metric.label,
      type: columnType,
      ...(columnWidth ? { width: columnWidth } : { minWidth: DEFAULT_METRIC_COLUMN_WIDTH }),
      align: "right",
      headerAlign: "right",
      sortable: false, // We handle sorting manually
      valueGetter: createValueGetter(metric, columnType),
      renderCell: (params) => (
        <MetricCellComponent
          params={params}
          metric={metric}
        />
      ),
      renderHeader: (params) => (
        <CustomHeaderComponent
          field={metric.id}
          colDef={params.colDef}
          sortMetricId={sortMetricId}
          isAbsoluteSort={isAbsoluteSort}
          sortArrowDirection={sortArrowDirection}
          metricMetadata={metricMetadata}
          onSortChange={onSortChange}
          data={data}
        />
      ),
      cellClassName: createCellClassName(metric, metricCategories, groupEndFieldSet),
      headerClassName: classNames({
        "first-instrument": metric.isFirstInstrument,
        "group-start": metric.metricIndex === 0,
        "group-end": (groupEndFieldSet?.has?.(metric.id))
          || (((metricCategories?.[metric.category]?.nbMetrics ?? 0) > 0)
            ? metric.metricIndex === (metricCategories?.[metric.category]?.nbMetrics - 1)
            : false),
      }),
      pinnable: true,
      ...(metric.category && { groupId: metric.category }),
    }
  })
}
